import { render as toPlainText } from "datocms-structured-text-to-plain-text"
import { DateTime } from "luxon"
import { useTranslation } from "next-i18next"
import React from "react"
import { Image as ResponsiveImage, StructuredText } from "react-datocms"
import styled from "styled-components"
import { Col, Row } from "react-styled-flexboxgrid"
import { truncate } from "@aistihealth/web-common/src/util/strings"

import { ArticleCard } from "../article-card/ArticleCard"
import { Heading } from "../heading/Heading"
import { Section, SectionProps } from "../section/Section"
import { SingleColLayout } from "../single-col-layout/SingleColLayout"
import {
  GetFrontPageQuery,
  HeaderTextSectionFieldsFragment,
} from "../../datocms/gql/generated/graphql"
import { omitParagraphTagRenderRule } from "../../datocms/nodeRules"
import { theme } from "../../util/styles/theme"

const CONTENT_MAX_LENGTH = 200 // chars

interface BlogsSectionProps {
  blogPosts: GetFrontPageQuery["allArticles"]
  blogSection: HeaderTextSectionFieldsFragment
  sectionProps: SectionProps
}

const ArticleCardCol = styled(Col)`
  margin-bottom: ${theme.spacings.medium};
`

export const BlogsSection: React.FunctionComponent<BlogsSectionProps> = ({
  blogPosts,
  blogSection,
  sectionProps,
}) => {
  const { t } = useTranslation()
  const onlyTwoPosts = blogPosts.length === 2
  const { backgroundColor, bottomCut } = sectionProps

  return (
    <Section backgroundColor={backgroundColor} bottomCut={bottomCut}>
      <Heading as="h2" size="xlarge" align="center">
        <StructuredText
          data={blogSection.header.value}
          customNodeRules={[omitParagraphTagRenderRule]}
        />
      </Heading>
      <SingleColLayout
        colProps={{
          lg: onlyTwoPosts ? 10 : 12,
          md: onlyTwoPosts ? 10 : 12,
          sm: 8,
          xs: 12,
        }}
        rowProps={{ center: "xs" }}
      >
        <Row>
          {blogPosts.map(p => {
            const image = (
              <ResponsiveImage
                data={p.image.responsiveImage}
                style={{ borderRadius: theme.borderRadius.medium }}
              />
            )
            const content = truncate(
              toPlainText(p.content.value),
              CONTENT_MAX_LENGTH,
            )

            return (
              <ArticleCardCol
                key={p.id}
                lg={onlyTwoPosts ? 6 : 4}
                md={onlyTwoPosts ? 6 : 4}
                sm={12}
                xs={12}
              >
                <ArticleCard
                  key={p.id}
                  title={p.title}
                  date={DateTime.fromISO(p.date).toFormat(
                    t("common:formats.date.short"),
                  )}
                  orientation={{ xs: "vertical" }}
                  image={image}
                  content={content}
                  href={`/blog/${encodeURIComponent(p.slug)}`}
                />
              </ArticleCardCol>
            )
          })}
        </Row>
      </SingleColLayout>
    </Section>
  )
}
